import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Shield, Clock, CheckCircle2, ArrowRight,
  Star, MapPin, Building2, CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useI18n } from "@/context/I18nContext";
import { api, unwrap } from "@/lib/api";
import WhatsAppFAB from "@/components/shared/WhatsAppFAB";
import BeforeAfterCarousel from "@/components/shared/BeforeAfterCarousel";
import WhatWeCleanCarousel from "@/components/shared/WhatWeCleanCarousel";
import { QuickQuoteBar } from "@/components/shared/QuickQuoteBar";
import SEO from "@/components/shared/SEO";
import { landingSchema } from "@/lib/seo";

// Verified Pexels photography (placeholders until the client supplies real shots).
const HERO_IMG = "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg"; // warm cozy living room
const PRO_IMG = "https://images.pexels.com/photos/9462206/pexels-photo-9462206.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=640&h=800&dpr=2"; // smiling, uniformed cleaning professional — sharp, right-sized crop
const HOW_VIDEO_POSTER = "https://images.pexels.com/photos/4108715/pexels-photo-4108715.jpeg"; // warm in-action
// "What we clean" — one verified photo per service (order matches services.items)
const CLEAN_PHOTOS = [
  "https://images.pexels.com/photos/2724748/pexels-photo-2724748.jpeg", // Kitchens
  "https://images.pexels.com/photos/4239130/pexels-photo-4239130.jpeg", // Bathrooms
  "https://images.pexels.com/photos/6585757/pexels-photo-6585757.jpeg", // Bedrooms & living rooms
  "https://images.pexels.com/photos/5591581/pexels-photo-5591581.jpeg", // Laundry
  "https://images.pexels.com/photos/4239146/pexels-photo-4239146.jpeg", // Windows
  "https://images.pexels.com/photos/4108715/pexels-photo-4108715.jpeg", // Move-in / Move-out
];
// Before/After rotating gallery — order matches before_after.rooms
const BEFORE_AFTER_PHOTOS = [
  "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg", // Living room
  "https://images.pexels.com/photos/2724748/pexels-photo-2724748.jpeg", // Kitchen
  "https://images.pexels.com/photos/6585757/pexels-photo-6585757.jpeg", // Bedroom
  "https://images.pexels.com/photos/4239130/pexels-photo-4239130.jpeg", // Bathroom
  "https://images.pexels.com/photos/4239146/pexels-photo-4239146.jpeg", // Windows
];
// "Real cleanings, real homes" — people-focused verified Pexels photography
const REAL_CLEANING_PHOTOS = [
  "https://images.pexels.com/photos/5591581/pexels-photo-5591581.jpeg",   // folding laundry
  "https://images.pexels.com/photos/4108715/pexels-photo-4108715.jpeg",   // cleaning kitchen
  "https://images.pexels.com/photos/4239013/pexels-photo-4239013.jpeg",   // vacuuming / living room
  "https://images.pexels.com/photos/4239130/pexels-photo-4239130.jpeg",   // bathroom
  "https://images.pexels.com/photos/4107284/pexels-photo-4107284.jpeg",   // organizing
  "https://images.pexels.com/photos/9462206/pexels-photo-9462206.jpeg",   // vetted pro
];

const AVATARS = [
  "https://images.unsplash.com/photo-1504199367641-aba8151af406?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  "https://images.unsplash.com/photo-1562337404-3044c84ac061?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  "https://images.unsplash.com/photo-1594756154841-ac5d160dbf46?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function LandingPage() {
  const { t } = useI18n();
  const [zones, setZones] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [startingAt, setStartingAt] = useState(89);
  const [reviewsStats, setReviewsStats] = useState({ count: 0, avg: null });

  useEffect(() => {
    api.get("/zones").then((r) => setZones(unwrap(r) || [])).catch(() => {});
    api.get("/reviews/public?limit=6").then((r) => {
      const list = unwrap(r) || [];
      setReviews(list);
      if (list.length > 0) {
        const sum = list.reduce((acc, x) => acc + (x.rating || 0), 0);
        setReviewsStats({ count: list.length, avg: sum / list.length });
      }
    }).catch(() => {});
    api.get("/pricing/active").then((r) => {
      const rule = unwrap(r) || {};
      const minOrder = Number(rule.minimum_order || rule.base_price || 89);
      if (minOrder > 0) setStartingAt(Math.round(minOrder));
    }).catch(() => {});
  }, []);

  const trust = [
    { icon: Sparkles, label: t("trust.years") },
    { icon: Shield, label: t("trust.insured") },
    { icon: CheckCircle2, label: t("trust.guarantee") },
    { icon: Clock, label: t("trust.online") },
  ];

  const whatWeCleanSlides = (t("services.items") || []).map((s, i) => ({
    image: CLEAN_PHOTOS[i % CLEAN_PHOTOS.length],
    title: s.t,
    desc: s.d,
  }));
  const beforeAfterPairs = (t("before_after.rooms") || []).map((room, i) => ({
    image: BEFORE_AFTER_PHOTOS[i % BEFORE_AFTER_PHOTOS.length],
    room,
  }));
  const realSlides = (t("services.items") || []).map((s, i) => ({
    image: REAL_CLEANING_PHOTOS[i % REAL_CLEANING_PHOTOS.length],
    title: s.t,
    desc: s.d,
  }));

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const schemaLd = landingSchema({ origin, reviewsCount: reviewsStats.count, ratingAvg: reviewsStats.avg });

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <SEO
        title="Ever Clean Home — Residential Cleaning in Boston, CT, RI & Providence"
        description="Trusted residential cleaning across Boston, Cambridge, Providence, Connecticut, Rhode Island. Book online in 60 seconds, pay securely, zero phone calls."
        jsonLd={[schemaLd]}
      />
      <Header />
      <WhatsAppFAB label={t("whatsapp.chat")} />

      {/* QUICK ZIP QUOTE — 60-second entry, sits above the hero */}
      <QuickQuoteBar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#FBF4E8] via-[#FAF6EF] to-[#FAFAF9]" data-testid="hero-section">
        <div className="absolute inset-0 bg-grain pointer-events-none opacity-60" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#FACC15]/20 blur-3xl pointer-events-none" />
        <div className="absolute top-40 -left-24 w-80 h-80 rounded-full bg-[#0EA5A4]/15 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-16 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0EA5A4]/10 text-[#0F766E] text-xs font-semibold mb-6" data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5" /> {t("hero.badge")}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] text-[#0C1A1A]">
              {t("hero.title_1")} <br />
              <span className="text-[#0EA5A4]">{t("hero.title_2")}</span> <br />
              <span className="relative inline-block">
                {t("hero.title_3")}
                <span className="absolute -bottom-2 left-0 right-0 h-2 bg-[#FACC15] -z-10 rounded-full" />
              </span>
            </h1>
            <p className="mt-6 text-lg text-[#4B5563] max-w-xl leading-relaxed">{t("hero.subtitle")}</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/book">
                <Button size="lg" className="bg-[#0EA5A4] hover:bg-[#0F766E] text-white rounded-xl px-7 h-14 text-base font-semibold shadow-lg shadow-[#0EA5A4]/20 hover:scale-[1.02] transition-transform" data-testid="hero-cta-primary">
                  {t("hero.cta_primary")} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button size="lg" variant="outline" className="rounded-xl h-14 px-7 border-[#0C1A1A]/15 hover:bg-[#0C1A1A]/5" data-testid="hero-cta-secondary">
                  {t("hero.cta_secondary")}
                </Button>
              </a>
            </div>

            {/* Availability ribbon */}
            <div className="mt-6 max-w-xl rounded-xl bg-[#A7F3D0]/55 px-5 py-3 flex items-center justify-center gap-2.5 text-center" data-testid="hero-availability-banner">
              <CalendarClock className="w-5 h-5 text-[#0F766E] shrink-0" />
              <span className="text-[#0F766E] font-medium text-[15px] md:text-base">{t("hero.availability_banner")}</span>
            </div>

            {/* Trust badges */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="trust-badges">
              {trust.map((it, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 * i + 0.3 }}
                  className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-8 h-8 rounded-full bg-[#0EA5A4]/10 grid place-items-center text-[#0F766E] shrink-0">
                    <it.icon className="w-4 h-4" />
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-[#0C1A1A] leading-snug">{it.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Hero image composition */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-5 relative">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-[#0EA5A4]/20 to-[#FACC15]/25 rounded-[2.5rem] -z-10 rotate-1" />
              <img src={HERO_IMG} alt="Warm, spotless living room cleaned by Ever Clean Home"
                className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/5]" />

              {/* Real professional — projects trust ("our vetted team in your home") */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
                className="absolute -bottom-7 -left-5 sm:-left-8 w-32 sm:w-40 rounded-2xl shadow-2xl border-4 border-white overflow-hidden bg-white" data-testid="hero-pro-photo">
                <img src={PRO_IMG} alt="Insured, background-checked Ever Clean Home cleaning professional"
                  className="w-full aspect-[3/4] object-cover object-top" />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0C1A1A]/85 to-transparent px-2.5 py-2">
                  <div className="text-[10px] font-bold text-white leading-tight">{t("hero.pro_badge")}</div>
                </div>
              </motion.div>

              {/* Floating rating card */}
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}
                className="absolute -bottom-5 -right-3 sm:-right-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-[#E5E7EB]">
                <div className="flex -space-x-2">
                  {AVATARS.map((a, i) => (
                    <img key={i} src={a} alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 text-[#FACC15]">
                    {[...Array(5)].map((_, i) => <Star key={i} fill="currentColor" className="w-3.5 h-3.5" />)}
                  </div>
                  <div className="text-xs font-semibold text-[#0C1A1A]">{t("hero.rating_label")}</div>
                </div>
              </motion.div>
              {/* Floating price card */}
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}
                className="absolute -top-4 -right-3 sm:-right-6 bg-white rounded-2xl shadow-xl p-3 px-4 border border-[#E5E7EB]">
                <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold">{t("hero.instant_quote")}</div>
                <div className="font-mono text-2xl font-bold text-[#0EA5A4]">$129<span className="text-sm text-[#4B5563]">/avg</span></div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRICING TRANSPARENCY STRIP */}
      <section className="py-12 border-y border-[#E5E7EB] bg-white" data-testid="pricing-strip">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
          <div className="md:col-span-1">
            <div className="text-xs uppercase tracking-wider text-[#0EA5A4] font-bold">{t("pricing_strip.eyebrow")}</div>
            <div className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter mt-1">{t("pricing_strip.starting_at")} <span className="font-mono">${startingAt}</span></div>
            <p className="text-sm text-[#4B5563] mt-2">{t("pricing_strip.note")}</p>
          </div>
          <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: t("pricing_strip.studio"), price: `${t("pricing_strip.from")} $${startingAt}` },
              { label: t("pricing_strip.apt_1"), price: `${t("pricing_strip.from")} $${startingAt + 40}` },
              { label: t("pricing_strip.apt_2"), price: `${t("pricing_strip.from")} $${startingAt + 90}` },
              { label: t("pricing_strip.house_3"), price: `${t("pricing_strip.from")} $${startingAt + 160}` },
            ].map((p, i) => (
              <div key={i} className="rounded-xl border border-[#E5E7EB] p-3 hover:border-[#0EA5A4] transition-colors text-center" data-testid={`pricing-card-${i}`}>
                <div className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold">{p.label}</div>
                <div className="font-mono font-bold text-[#0EA5A4] mt-1">{p.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 md:py-28 bg-[#FBF6EC]" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{t("how.title")}</h2>
            <p className="mt-3 text-lg text-[#4B5563]">{t("how.subtitle")}</p>
          </motion.div>

          {/* Explainer video — autoplay, muted, looping; controls on hover */}
          <HowItWorksVideo poster={HOW_VIDEO_POSTER} eyebrow={t("how.video_eyebrow")} caption={t("how.video_caption")} />

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <motion.div key={n} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: n * 0.1 }}>
                <Card className="rounded-2xl border border-[#E5E7EB] bg-white p-7 h-full hover:shadow-lg transition-shadow" data-testid={`how-step-${n}`}>
                  <div className="font-mono text-5xl font-bold text-[#0EA5A4]/30">0{n}</div>
                  <h3 className="font-display text-xl font-bold mt-4">{t(`how.step${n}_title`)}</h3>
                  <p className="mt-2 text-[#4B5563] text-sm leading-relaxed">{t(`how.step${n}_desc`)}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES — auto-playing photo carousel */}
      <section className="py-20 md:py-28 bg-[#F0FBFA]" data-testid="services-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0EA5A4]/10 text-[#0F766E] text-xs font-semibold mb-4">
              <Sparkles className="w-3.5 h-3.5" /> {t("services.title")}
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{t("services.title")}</h2>
            <p className="mt-3 text-lg text-[#4B5563]">{t("services.subtitle")}</p>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <WhatWeCleanCarousel slides={whatWeCleanSlides} />
          </motion.div>
        </div>
      </section>

      {/* FOR AIRBNB HOSTS */}
      <section className="py-12 md:py-16 bg-white" data-testid="airbnb-strip-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="rounded-[2rem] overflow-hidden border border-[#0EA5A4]/20 bg-gradient-to-br from-[#0EA5A4]/10 via-[#0EA5A4]/[0.06] to-[#FACC15]/10 p-8 md:p-12 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0EA5A4]/15 text-[#0F766E] text-xs font-bold uppercase tracking-wider mb-4">
                <Building2 className="w-3.5 h-3.5" /> {t("landing.airbnb_eyebrow")}
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter leading-tight">{t("landing.airbnb_title")}</h2>
              <p className="mt-3 text-[#4B5563] text-base md:text-lg max-w-xl">{t("landing.airbnb_sub")}</p>
            </div>
            <div className="md:text-right">
              <Link to="/for-hosts">
                <Button size="lg" className="bg-[#0EA5A4] hover:bg-[#0F766E] text-white rounded-xl h-13 px-6 font-semibold hover:scale-[1.02] transition-transform" data-testid="airbnb-strip-cta">
                  {t("landing.airbnb_cta")} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* REAL CLEANINGS, REAL HOMES */}
      <section className="py-16 md:py-24 bg-[#FBF6EC]" data-testid="real-cleanings-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FACC15]/20 text-[#0C1A1A] text-xs font-semibold mb-4">
              <Sparkles className="w-3.5 h-3.5" /> {t("landing.real_eyebrow")}
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{t("landing.real_title")}</h2>
            <p className="mt-3 text-lg text-[#4B5563]">{t("landing.real_sub")}</p>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <WhatWeCleanCarousel slides={realSlides} />
          </motion.div>
        </div>
      </section>

      {/* BEFORE/AFTER */}
      <section className="py-20 md:py-28 bg-white" data-testid="before-after-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FACC15]/20 text-[#0C1A1A] text-xs font-semibold mb-4">
              <Sparkles className="w-3.5 h-3.5" /> {t("before_after.chip")}
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter leading-tight">
              {t("before_after.title_1")} <br />{t("before_after.title_2")}
            </h2>
            <p className="mt-4 text-[#4B5563] text-lg leading-relaxed">
              {t("before_after.body")}
            </p>
            <ul className="mt-5 space-y-2.5 text-sm">
              {t("before_after.bullets").map((it, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-[#0EA5A4] shrink-0 mt-0.5" />
                  <span className="text-[#0C1A1A]">{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <BeforeAfterCarousel
            pairs={beforeAfterPairs}
            beforeLabel={t("before_after.before")}
            afterLabel={t("before_after.after")}
          />
        </div>
      </section>

      {/* ZONES */}
      <section className="py-20 md:py-28 bg-[#0C1A1A] text-white relative overflow-hidden" data-testid="zones-section">
        <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-semibold mb-5">
              <MapPin className="w-3.5 h-3.5" /> {t("zones.coverage")}
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{t("zones.title")}</h2>
            <p className="mt-3 text-lg text-white/70">{t("zones.subtitle")}</p>
            <Link to="/book" className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-xl bg-[#FACC15] text-[#0C1A1A] font-semibold hover:scale-[1.02] transition-transform">
              {t("hero.cta_primary")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {zones.map((z) => (
              <div key={z.id} className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:bg-white/10 transition-colors" data-testid={`zone-card-${z.state}`}>
                <div className="text-xs font-mono uppercase tracking-wider text-[#FACC15]">{z.state}</div>
                <div className="mt-1 font-display text-xl font-bold">{z.name}</div>
                <div className="mt-2 text-xs text-white/50 line-clamp-2">{(z.cities || []).slice(0, 4).join(" · ")}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{t("testimonials.title")}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {(reviews.length > 0 ? reviews.slice(0, 3) : t("testimonials.items").map((it, i) => ({
              comment: it.quote, customer_name: it.name, customer_city: it.city, rating: 5,
              _placeholder: true, _avatar: AVATARS[i],
            }))).map((it, i) => (
              <motion.div key={it.id || i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Card className="rounded-2xl p-7 h-full border border-[#E5E7EB]" data-testid={`testimonial-${i}`}>
                  <div className="flex text-[#FACC15] mb-3">
                    {[...Array(5)].map((_, k) => (
                      <Star key={k} fill={k < (it.rating || 5) ? "currentColor" : "none"} stroke="currentColor" className="w-4 h-4" />
                    ))}
                  </div>
                  <p className="text-[#0C1A1A] leading-relaxed">&ldquo;{it.comment || it.quote}&rdquo;</p>
                  {it.admin_response && (
                    <div className="mt-3 p-3 rounded-lg bg-[#0EA5A4]/5 text-xs text-[#0F766E]">
                      <span className="font-bold uppercase tracking-wider text-[10px]">{t("testimonials.reply_label")}</span>
                      <div className="mt-1 text-[#0C1A1A]">{it.admin_response}</div>
                    </div>
                  )}
                  <div className="mt-5 flex items-center gap-3">
                    {it._avatar && <img src={it._avatar} alt="" className="w-10 h-10 rounded-full object-cover" />}
                    <div>
                      <div className="font-semibold text-sm">{it.customer_name || it.name}</div>
                      <div className="text-xs text-[#4B5563]">{it.customer_city || it.city}</div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ resumido */}
      <section className="py-20 md:py-28 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter text-center">{t("faq.title")}</h2>
          <Accordion type="single" collapsible className="mt-10">
            {t("faq.items").slice(0, 5).map((q, i) => (
              <AccordionItem key={i} value={`q-${i}`} className="border-b border-[#E5E7EB]" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display font-semibold text-base hover:text-[#0EA5A4]">{q.q}</AccordionTrigger>
                <AccordionContent className="text-[#4B5563] whitespace-pre-line">{q.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 text-center">
            <Link to="/faq" className="text-[#0EA5A4] font-semibold hover:underline" data-testid="see-all-faq">
              {t("faq.see_all")}
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="rounded-[2.5rem] bg-[#0EA5A4] text-white p-10 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-grain opacity-30" />
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter leading-tight">{t("final_cta.title_1")}<br />{t("final_cta.title_2")}</h3>
                <p className="mt-3 text-white/85 max-w-md">{t("final_cta.body")}</p>
              </div>
              <div className="md:text-right">
                <Link to="/book">
                  <Button size="lg" className="bg-[#FACC15] text-[#0C1A1A] hover:bg-[#FACC15]/90 rounded-xl h-14 px-7 font-semibold hover:scale-[1.02] transition-transform" data-testid="final-cta">
                    {t("hero.cta_primary")} <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}


function HowItWorksVideo({ poster, eyebrow, caption }) {
  const [hover, setHover] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.6 }} className="mt-10" data-testid="how-video">
      <div
        className="relative rounded-[2rem] overflow-hidden shadow-xl border border-[#E5E7EB] aspect-video bg-[#0C1A1A]"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/static/videos/ever-clean-home-v3.mp4"
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          controls={hover}
          data-testid="how-video-player"
        />
      </div>
      <div className="mt-5 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0EA5A4]/10 text-[#0F766E] text-xs font-semibold uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5" /> {eyebrow}
        </div>
        <div className="mt-2 font-display text-lg sm:text-xl font-bold text-[#0C1A1A]" data-testid="how-video-caption">{caption}</div>
      </div>
    </motion.div>
  );
}
